import React, { useEffect, useState } from 'react';

import { cn } from '@ui/lib/utils';

/** Whether an exit animation will run, and so whether its end will ever be heard. */
export const canAnimate = (): boolean =>
  typeof window.matchMedia === 'function' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface PresenceProps {
  show: boolean;
  /** Animation classes for appearing, e.g. `animate-in fade-in-0`. */
  enter: string;
  /** Animation classes for leaving; the content stays mounted until they finish. */
  exit: string;
  className?: string;
  children: React.ReactNode;
}

/** Shows its content with an enter animation and keeps it on screen through an exit animation. */
const Presence = ({ show, enter, exit, className, children }: PresenceProps): JSX.Element | null => {
  const [mounted, setMounted] = useState(show);

  useEffect(() => {
    if (show) setMounted(true);
    else if (!canAnimate()) setMounted(false);
  }, [show]);

  if (!mounted) return null;

  return (
    <div
      className={cn(
        className,
        'motion-reduce:animate-none',
        show ? enter : cn('pointer-events-none fill-mode-forwards', exit),
      )}
      // Portalled children bubble their own animations through here; only this element's own end counts.
      onAnimationEnd={event => {
        if (!show && event.target === event.currentTarget) setMounted(false);
      }}
    >
      {children}
    </div>
  );
};

export default Presence;
